import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import {
  Row, Col, Typography, Card, Form, Button, message,
  Input,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import PropTypes from 'prop-types';
import MaskedInput from 'react-text-mask';
import ArrowLeftIcon from 'mdi-react/ArrowLeftIcon';
import { getUserMail, getUserType } from '../../../../../utils/users';
import SelectedLands from '../../../serviceComponents/selectedLands/SelectedLands';
import GeoMapSummary from '../../../serviceComponents/geoMapSummary/GeoMapSummary';
import AreaTalhao from '../../../../sharedComponents/talhao/AreaTalhao';
import UploadFiles from '../../../serviceComponents/uploadFiles/UploadFiles';
import { SessionExpiredException } from '../../../../../utils/errors';
import { isItEstimateOrService } from '../../../../../utils/service';
import {
  post, get, isSuperAgentError,
  RequestWrapper, ResponseWrapper,
} from '../../../../../dataSource/requests';
import './Summary.scss';

const Summary = (props): JSX.Element => {
  const [formInstance] = Form.useForm();
  const navigate = useNavigate();
  const userType = getUserType();

  const uploadFilesReference = useRef(null);
  const [valueAttachmentSizeCooperated, setValueAttachmentSizeCooperated] = useState(null);

  const [loading, setLoading] = useState(false);
  const [requestId, setRequestId] = useState(null);

  const currencyFormatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const titleOrcamento = useRef(false);

  const {
    removeSelectedField, serviceRequestParameters, nextPage, previousPage,
  } = props;

  const getLabel = (idCode: string, value): string => {
    switch (idCode) {
      case 'GEOFERT-COLETA-POR-TALHOES-VALOR_UNITARIO':
        if (value === '') return '';
        return 'Valor unitário';
      case 'GEOFERT-COLETA-POR-TALHOES-VALOR_TOTAL':
        if (value === '') return '';
        return 'Valor total';
      case 'GEOFERT-COLETA-POR-TALHOES-CONDICAO_PAGAMENTO':
        if (value === '') return '';
        return 'Condição de pagamento';
      case 'GEOFERT-COLETA-POR-TALHOES-FORMA_PAGAMENTO':
        if (value === '') return '';
        return 'Forma de pagamento';
      case 'GEOFERT-COLETA-POR-TALHOES-OBSERVACOES':
        if (value === '') return '';
        return 'Observações do orçamento';
      default:
        return '';
    }
  };

  const getField = (idCodeParameter: string): any => {
    return serviceRequestParameters
      .serviceRequestFieldInputList
      .find(
        (field) => {
          return field.idCode === idCodeParameter;
        },
      );
  };

  const printValue = (idCode: string, value): any => {
    switch (true) {
      case value === true:
      case value === 'true':
        return 'Sim';

      case value === false:
      case value === 'false':
        return 'Não';

      case (value?._isAMomentObject):
        return value.format('DD/MM/YYYY');

      case (/[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}.[0-9]{3}Z/.test(value)):
        return moment(value, 'YYYY-MM-DDTHH:mm:ss.SSS[Z]').format('DD/MM/YYYY');

      case (idCode === 'GEOFERT-COLETA-POR-TALHOES-VALOR_UNITARIO' && value !== ''):
        return currencyFormatter.format(value);

      case (idCode === 'GEOFERT-COLETA-POR-TALHOES-VALOR_TOTAL' && value !== ''):
        return currencyFormatter.format(value);

      default:
        return value;
    }
  };

  const renderField = (idCodeParameter: string): JSX.Element | null => {
    const field = getField(idCodeParameter);

    if (field !== undefined) {
      if (idCodeParameter === 'GEOFERT-COLETA-POR-TALHOES-VALOR_UNITARIO' && field.value !== '') {
        titleOrcamento.current = true;
      }

      return (
        <Row
          className="ColetaPorTalhoesSummary__DetailRow"
        >
          <Col
            span={24}
            className="ColetaPorTalhoesSummary__DetailParentColumn"
          >

            {
              idCodeParameter === 'GEOFERT-COLETA-POR-TALHOES-VALOR_UNITARIO' && field.value !== ''
              && (
                <>
                  <hr />
                  <p className="ColetaPorTalhoesSummary__SessionTitle">Orçamento</p>
                </>
              )
            }

            {
              titleOrcamento.current === false
              && idCodeParameter === 'GEOFERT-COLETA-POR-TALHOES-OBSERVACOES'
              && field.value !== ''
              && (
                <>
                  <hr />
                  <p className="ColetaPorTalhoesSummary__SessionTitle">Orçamento</p>
                </>
              )
            }

            <Row
              justify="space-between"
              className="ColetaPorTalhoesSummary__DetailChildRow"
            >
              <Col
                className="ColetaPorTalhoesSummary__DetailColumn"
              >
                {getLabel(idCodeParameter, field.value)}
              </Col>
              <Col
                className="ColetaPorTalhoesSummary__DetailSpecificColumn"
              >
                {printValue(idCodeParameter, field.value)}
              </Col>
            </Row>
          </Col>
        </Row>
      );
    }

    return null;
  };

  const mask = (rawValue): (string | RegExp)[] => {
    if (rawValue.length > 14) {
      return [
        '(',
        /[1-9]/,
        /\d/,
        ')',
        ' ',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        '-',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
      ];
    }
    return [
      '(',
      /[1-9]/,
      /\d/,
      ')',
      ' ',
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      '-',
      /\d/,
      /\d/,
      /\d/,
      /\d/,
    ];
  };

  const getAttachmentParamenters = async (): Promise<void> => {
    try {
      const requestRef: RequestWrapper = get({
        url: '/system/getAttachmentParamenters',

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      const response = await requestRef;

      setValueAttachmentSizeCooperated(
        response?.body.getAttachmentParamentersOutput.attachmentSizeCooperated,
      );
    } catch (error) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
      message.error('Ocorreu um erro ao buscar informações, tente novamente');
    }
  };

  useEffect(() => {
    getAttachmentParamenters();
    return () => {
      setLoading(false);
    };
  }, []);

  const userMailCooperative = (): JSX.Element => {
    const targetMail = getUserMail() ? getUserMail()?.replaceAll('"', '') : '-';

    return (
      <div>
        <div>
          <span>{`Email: ${userType === 'COOPERATIVE' ? targetMail : '-'}`}</span>
        </div>
        <div>
          <span style={{ fontSize: '.7rem' }}>(caso esteja incorreto, informar e-mail válido no campo observação)</span>
        </div>
      </div>
    );
  };

  const onFormFinish = async (values): Promise<void> => {
    setLoading(true);
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    let telValue = values.TelInput;
    telValue = !telValue ? '' : telValue.replace(/\D/g, '');

    const data = {
      landFieldsIds: serviceRequestParameters.talhoesSelecionados.map((v) => v.id),
      landId: serviceRequestParameters.selectedProperty.id,
      landOwnerId: serviceRequestParameters.selectedOwner.id,
      serviceId: serviceRequestParameters.selectedService.id,
      cooperativeNotes: values.observacao,
      telephone: telValue,
      serviceTypeId: serviceRequestParameters.selectedType.id,
      haveCtc: serviceRequestParameters.haveCtc,
      ctc: serviceRequestParameters.ctc,
      branchCoopercitrusId: serviceRequestParameters.branchCoopercitrusId,
    };

    if (userType === 'COOPERATIVE') {
      serviceRequestParameters.serviceRequestFieldInputList.push({
        idCode: 'GEOFERT-COLETA-POR-TALHOES-VALOR_UNITARIO',
        value: '',
      });
      serviceRequestParameters.serviceRequestFieldInputList.push({
        idCode: 'GEOFERT-COLETA-POR-TALHOES-VALOR_TOTAL',
        value: '',
      });
      serviceRequestParameters.serviceRequestFieldInputList.push({
        idCode: 'GEOFERT-COLETA-POR-TALHOES-CONDICAO_PAGAMENTO',
        value: '',
      });
      serviceRequestParameters.serviceRequestFieldInputList.push({
        idCode: 'GEOFERT-COLETA-POR-TALHOES-FORMA_PAGAMENTO',
        value: '',
      });
      serviceRequestParameters.serviceRequestFieldInputList.push({
        idCode: 'GEOFERT-COLETA-POR-TALHOES-OBSERVACOES',
        value: '',
      });
    }

    let targetUrl = '';
    if (serviceRequestParameters.choiceServiceType === 1) {
      targetUrl = '/budget/requestBudgetService';
      data.budgetRequestFieldInputs = serviceRequestParameters.serviceRequestFieldInputList;
    } else {
      targetUrl = '/service/requestService';
      data.serviceRequestFieldInputList = serviceRequestParameters.serviceRequestFieldInputList;
    }

    try {
      const requestRef: RequestWrapper = post({
        url: targetUrl,
        params: data,

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      response = await requestRef;
    } catch (error) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
      if (!(error instanceof SessionExpiredException)) {
        message.error('Ocorreu um erro ao solicitar o serviço, tente novamente');
      }
      setLoading(false);
      return;
    }

    if (!response.error) {
      if (response?.body.code === 5002) {
        flushSync(() => {
          setRequestId(response?.body.createServiceRequestOutput.serviceRequestId);
        });
      } else {
        flushSync(() => {
          setRequestId(response.createBudgetOutput.budgetId);
        });
      }

      const uploadError = !uploadFilesReference.current.validateFiles();
      if (!uploadError) {
        try {
          await uploadFilesReference.current.handleSubmitFiles();
        } catch (error) {
          setLoading(false);
          return;
        }

        nextPage({});
      }
    } else if (response.code === -6006) {
      message.error('Esta cultura não é válida para este serviço');
    } else {
      message.error('Ocorreu um erro ao solicitar o serviço, tente novamente');
    }

    setLoading(false);
  };

  return (
    <div className="ColetaPorTalhoesSummary">
      <Row
        justify="space-between"
        className="ColetaPorTalhoesSummary__TitleRow"
      >
        <Col
          className="ColetaPorTalhoesSummary__TitleCol"
        >
          <Typography.Title
            level={1}
            className="ColetaPorTalhoesSummary__Title"
          >
            Resumo da Solicitação
          </Typography.Title>
        </Col>
      </Row>

      <Row
        gutter={[24, 24]}
        className="ColetaPorTalhoesSummary__ContentRow"
      >
        <Col
          span={24}
          className="ColetaPorTalhoesSummary__ContentColumn"
        >
          <Card
            className="ColetaPorTalhoesSummary__ContentCard"
          >
            <Form
              scrollToFirstError
              className="ColetaPorTalhoesSummary__Form"
              form={formInstance}
              onFinish={onFormFinish}
              layout="vertical"
              onFinishFailed={
                (errorInfo): void => {
                  console.log('coleta por talhoes summary form finish failed:', errorInfo);
                }
              }
            >
              <Row
                gutter={[24, 24]}
                className="ColetaPorTalhoesSummary__DetailsRow"
              >
                <Col
                  xs={{ span: 24 }}
                  lg={{ span: 12 }}
                  className="ColetaPorTalhoesSummary__DetailsColumn"
                >
                  <Row
                    gutter={[24, 24]}
                    className="ColetaPorTalhoesSummary__ServiceTitleRow"
                  >
                    <Col
                      xs={{ span: 24 }}
                      className="ColetaPorTalhoesSummary__ServiceTitleCol"
                    >
                      <Typography.Title
                        level={1}
                        className="ColetaPorTalhoesSummary__ServiceFullTitle"
                      >
                        <span className="ColetaPorTalhoesSummary__ServiceTitle">{'Geofert: '}</span>
                        <span className="ColetaPorTalhoesSummary__ServicesTypeTitle">Coleta por talhões</span>
                      </Typography.Title>
                    </Col>
                  </Row>

                  <Row
                    gutter={[8, 8]}
                    className="ColetaPorTalhoesSummary__DetailRow ColetaPorTalhoesSummary__MatriculaRow"
                  >
                    <Col
                      className="ColetaPorTalhoesSummary__DetailColumn ColetaPorTalhoesSummary__MatriculaColumn"
                      xs={{ span: 24 }}
                    >
                      {`Matrícula: ${serviceRequestParameters.selectedProperty.matriculation}`}
                    </Col>
                  </Row>

                  <Row
                    gutter={[8, 8]}
                    className="ColetaPorTalhoesSummary__DetailRow ColetaPorTalhoesSummary__CooperadoRow"
                  >
                    <Col
                      xs={{ span: 24 }}
                      className="ColetaPorTalhoesSummary__DetailColumn ColetaPorTalhoesSummary__CooperadoColumn"
                    >
                      {`Cooperado: ${serviceRequestParameters.selectedOwner.name}`}
                    </Col>
                  </Row>

                  <Row
                    gutter={[8, 8]}
                    className="ColetaPorTalhoesSummary__DetailRow ColetaPorTalhoesSummary__PropriedadeRow"
                  >
                    <Col
                      xs={{ span: 24 }}
                      className="ColetaPorTalhoesSummary__DetailColumn ColetaPorTalhoesSummary__PropriedadeColumn"
                    >
                      {`Propriedade: ${serviceRequestParameters.selectedProperty.name}`}
                    </Col>
                  </Row>

                  <Row
                    gutter={[8, 8]}
                    className="ColetaPorTalhoesSummary__DetailRow ColetaPorTalhoesSummary__PropriedadeRow"
                  >
                    <Col
                      xs={{ span: 24 }}
                      className="ColetaPorTalhoesSummary__DetailColumn ColetaPorTalhoesSummary__PropriedadeColumn"
                    >
                      {
                        userMailCooperative()
                      }
                    </Col>
                  </Row>

                  <Row
                    gutter={[8, 8]}
                    className="ColetaPorTalhoesSummary__DetailRow ColetaPorTalhoesSummary__PropriedadeRow"
                  >
                    <Col
                      xs={{ span: 24 }}
                      className="ColetaPorTalhoesSummary__DetailColumn ColetaPorTalhoesSummary__PropriedadeColumn"
                    >
                      <span>{`Possui CTC: ${serviceRequestParameters.haveCtc ? 'Sim' : 'Não'}`}</span>
                    </Col>
                  </Row>

                  <Row
                    gutter={[8, 8]}
                    className="ColetaPorTalhoesSummary__DetailRow ColetaPorTalhoesSummary__PropriedadeRow"
                  >
                    <Col
                      xs={{ span: 24 }}
                      className="ColetaPorTalhoesSummary__DetailColumn ColetaPorTalhoesSummary__PropriedadeColumn"
                    >
                      <span>{`CTC: ${serviceRequestParameters.ctc ? serviceRequestParameters.ctc : '-'}`}</span>
                    </Col>
                  </Row>

                  <Row
                    gutter={[8, 8]}
                    className="ColetaPorTalhoesSummary__DetailRow ColetaPorTalhoesSummary__PropriedadeRow"
                  >
                    <Col
                      xs={{ span: 24 }}
                      className="ColetaPorTalhoesSummary__DetailColumn ColetaPorTalhoesSummary__PropriedadeColumn"
                    >
                      <span>{`Filial: ${serviceRequestParameters.branchName ? serviceRequestParameters.branchName : '-'}`}</span>
                    </Col>
                  </Row>

                  <Row
                    gutter={[8, 8]}
                    className="ColetaPorTalhoesSummary__DetailRow ColetaPorTalhoesSummary__PropriedadeRow"
                  >
                    <Col
                      xs={{ span: 24 }}
                      className="ColetaPorTalhoesSummary__DetailColumn ColetaPorTalhoesSummary__PropriedadeColumn"
                    >
                      {renderField('GEOFERT-COLETA-POR-TALHOES-VALOR_UNITARIO')}
                      {renderField('GEOFERT-COLETA-POR-TALHOES-VALOR_TOTAL')}
                      {renderField('GEOFERT-COLETA-POR-TALHOES-CONDICAO_PAGAMENTO')}
                      {renderField('GEOFERT-COLETA-POR-TALHOES-FORMA_PAGAMENTO')}
                      {renderField('GEOFERT-COLETA-POR-TALHOES-OBSERVACOES')}
                    </Col>
                  </Row>

                  <Row
                    gutter={[8, 8]}
                    className="ColetaPorTalhoesSummary__PhoneRow"
                  >
                    <Col
                      xs={{ span: 24 }}
                      className="ColetaPorTalhoesSummary__PhoneColumn"
                    >
                      <Typography.Title
                        level={4}
                        className="ColetaPorTalhoesSummary__PhoneTitle"
                      >
                        Telefone para contato
                      </Typography.Title>
                      <Form.Item
                        name="TelInput"
                        className="ColetaPorTalhoesSummary__PhoneFormItem"
                        validateTrigger={['onBlur']}
                        rules={[
                          {
                            required: true,
                            message: 'Por favor, insira um telefone.',
                          },
                          {
                            min: 14,
                            message: 'Telefone inválido',
                          },
                        ]}
                      >
                        <MaskedInput
                          guide={false}
                          mask={mask}
                          placeholder="Telefone"
                          className="ant-input ColetaPorTalhoesSummary__PhoneMaskedInput"
                          maxLength={15}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row
                    gutter={[8, 8]}
                    className="ColetaPorTalhoesSummary__CommentsRow"
                  >
                    <Col
                      xs={{ span: 24 }}
                      className="ColetaPorTalhoesSummary__CommentsColumn"
                    >
                      <Typography.Title
                        level={4}
                        className="ColetaPorTalhoesSummary__CommentsTitle"
                      >
                        Observação
                      </Typography.Title>
                      <Form.Item
                        className="ColetaPorTalhoesSummary__CommentsFormItem"
                        name="observacao"
                        rules={[
                          {
                            max: 1000,
                            message: 'A observação deve ter no máximo 1000 caractéres',
                          },
                        ]}
                      >
                        <Input.TextArea
                          className="obsArea ColetaPorTalhoesSummary__CommentsInput"
                          xs={{ span: 24 }}
                          lg={{ span: 10 }}
                          rows={4}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row
                    gutter={[8, 8]}
                    className="ColetaPorTalhoesSummary__AttachmentsRow"
                  >
                    <Col
                      xs={{ span: 24 }}
                      className="ColetaPorTalhoesSummary__AttachmentsColumn"
                    >
                      <Typography.Title
                        level={4}
                        className="ColetaPorTalhoesSummary__AttachmentsTitle"
                      >
                        Anexos do cooperado
                      </Typography.Title>
                      <Form.Item
                        name="anexo"
                        className="ColetaPorTalhoesSummary__AttachmentsFormItem"
                      >
                        <div className="attachments-items-container">
                          <UploadFiles
                            ref={uploadFilesReference}
                            origin="summary"
                            requestType={
                              isItEstimateOrService(
                                serviceRequestParameters.choiceServiceType === 1 ? 'ORCAMENTO' : 'SERVICO',
                                userType,
                              )
                            }
                            typeUser="SERVICE_REQUEST_CUSTOMER_FILE"
                            action="edit"
                            id={requestId}
                            fileSize={valueAttachmentSizeCooperated}
                            initialFiles={
                              [].map((item) => ({ ...item, uid: item.id, status: 'done' }))
                            }
                          />
                        </div>
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>

                <Col
                  xs={{ span: 24 }}
                  lg={{ span: 12 }}
                  className="ColetaPorTalhoesSummary__GeomapColumn"
                >
                  <SelectedLands
                    talhoesSelecionados={serviceRequestParameters.talhoesSelecionados}
                    removeSelectedField={removeSelectedField}
                  />

                  <div
                    className="ColetaPorTalhoesSummary__AreaTalhaoSpacing"
                  >
                  </div>

                  <AreaTalhao
                    talhoesSelecionados={serviceRequestParameters.talhoesSelecionados}
                  />

                  <GeoMapSummary
                    params={serviceRequestParameters}
                  />
                </Col>
              </Row>

              <Row
                gutter={[24, 24]}
                justify="end"
                className="ColetaPorTalhoesSummary__ButtonRow"
              >
                <Col
                  xs={{ span: 24 }}
                  md={{ span: 12 }}
                  lg={{ span: 6 }}
                  className="ColetaPorTalhoesSummary__ButtonFirstColumn"
                >
                  <Button
                    block
                    className="ColetaPorTalhoesSummary__BackButton"
                    onClick={
                      (): void => {
                        previousPage();
                      }
                    }
                    loading={loading}
                  >
                    <ArrowLeftIcon className="ColetaPorTalhoesSummary__ArrowLeftIcon" />
                    Voltar
                  </Button>
                </Col>
                <Col
                  xs={{ span: 24 }}
                  md={{ span: 12 }}
                  lg={{ span: 6 }}
                  className="ColetaPorTalhoesSummary__ButtonSecondColumn"
                >
                  <Form.Item
                    className="ColetaPorTalhoesSummary__ContinueButtonFormItem"
                  >
                    <Button
                      block
                      type="primary"
                      htmlType="submit"
                      className="ColetaPorTalhoesSummary__ContinueButton"
                      loading={loading}
                    >
                      Confirmar Solicitação
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

Summary.propTypes = {
  removeSelectedField: PropTypes.func.isRequired,
  nextPage: PropTypes.func.isRequired,
  previousPage: PropTypes.func.isRequired,

  serviceRequestParameters: PropTypes.shape({
    selectedSubType: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),

    haveCtc: PropTypes.bool,
    ctc: PropTypes.string,
    branchCoopercitrusId: PropTypes.number,
    choiceServiceType: PropTypes.number,
    branchName: PropTypes.string,

    serviceTypeDynamicFields: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        idCode: PropTypes.string,
      }),
    ).isRequired,

    serviceSubtypeDynamicFields: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        idCode: PropTypes.string,
      }),
    ).isRequired,

    service: PropTypes.string,

    serviceRequestFieldInputList: PropTypes.arrayOf(
      PropTypes.shape({
        idCode: PropTypes.string.isRequired,
        value: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
          PropTypes.instanceOf(moment),
          PropTypes.bool,
        ]),
      }),
    ),

    selectedProperty: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
      registeredOn: PropTypes.instanceOf(moment),
      matriculation: PropTypes.string,

      location: PropTypes.shape({
        lat: PropTypes.number,
        lon: PropTypes.number,
      }),
    }),

    selectedType: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),

    selectedService: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),

    selectedOwner: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
    }),

    talhoesSelecionados: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        area: PropTypes.number,
        culture: PropTypes.shape({}),
        details: PropTypes.string,
        landId: PropTypes.number,
        name: PropTypes.string,

        polygon: PropTypes.arrayOf(
          PropTypes.shape({
            lat: PropTypes.number,
            lon: PropTypes.number,
          }),
        ),
      }),
    ),
  }).isRequired,
};
Summary.defaultProps = {};

export default Summary;
